import { Link } from '@carto/meridian-ds/components'
import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch } from 'app/state'
import { ProcessingJob, JobStatusType } from 'features/jobs/types'
import JobItem from '../JobItem'
import { deleteExportingJobRequest } from 'features/jobs/state/jobsExportingSlice'
import SubscriptionExportDialog from 'features/data-explorer/ui/Resources/Actions/Subscription/SubscriptionExportDialog/SubscriptionExportDialog'
import { createDialogOpener } from 'features/dialogs/openDialog'

const openJobCancelDialog = createDialogOpener(() => import('../common/JobCancelDialog'))

interface JobItemProps {
  job: ProcessingJob
}

function JobExportingItem({ job }: JobItemProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false)

  const { status, message, tooltip } = useMemo(() => {
    switch (job.status?.status) {
      case JobStatusType.Success:
        return {
          status: JobStatusType.Success,
          message: intl.formatMessage({ id: `jobs.export.status.success` }),
          tooltip: intl.formatMessage({ id: `jobs.export.tooltip.success` })
        }
      case JobStatusType.Failure:
        return {
          status: JobStatusType.Failure,
          message: intl.formatMessage(
            { id: `jobs.export.status.failure` },
            {
              link: (
                <Link>
                  <FormattedMessage id="common.button.readMore" />
                </Link>
              )
            }
          ),
          tooltip: intl.formatMessage({ id: 'jobs.tooltip.error' })
        }
      default: // JobStatusType.Pending, JobStatusType.Running
        return {
          status: JobStatusType.Running,
          message: intl.formatMessage({ id: `jobs.export.status.running` }),
          tooltip: intl.formatMessage({ id: 'jobs.tooltip.running' })
        }
    }
  }, [job, intl])

  const handleClick = () => {
    if (status === JobStatusType.Success) {
      setExportDialogOpen(true)
    } else if (status === JobStatusType.Failure) {
      setExportDialogOpen(true)
    }
  }

  const handleCancelJob = () => {
    openJobCancelDialog({
      job,
      cancelJob
    })
  }

  const cancelJob = () => {
    if (job.connection) {
      dispatch(deleteExportingJobRequest({ job }))
    }
  }

  return (
    <>
      {status && (
        <JobItem
          status={status}
          name={job.response?.metadata?.name || intl.formatMessage({ id: 'jobs.customQuery' })}
          message={message}
          tooltip={tooltip}
          onClick={handleClick}
          padded={true}
          onCancel={handleCancelJob}
        />
      )}
      {job.response?.metadata?.table && (
        <SubscriptionExportDialog
          open={exportDialogOpen}
          table={job.response?.metadata?.table}
          jobId={job.jobId}
          onClose={() => setExportDialogOpen(false)}
        />
      )}
    </>
  )
}

export default JobExportingItem
